import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  InputAdornment,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Person as PersonIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  VpnKey as KeyIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { apiList, invokeApi } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { useCookies } from "react-cookie";

const CustomerList = () => {
  const [cookies] = useCookies();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog States
  const [viewCustomer, setViewCustomer] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [deleteCustomer, setDeleteCustomer] = useState(null);
  const [passwordCustomer, setPasswordCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Edit Form Fields
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    status: "active",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  // Password Form Fields
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const demoCustomers = [
    {
      id: "CUST-1001",
      name: "Vijayakumar G",
      email: "vijayakumar@gmail.com",
      phone: "+91 91766 58923",
      roles: ["Customer"],
      status: "active",
      orders_count: 2,
      total_spent: 1200,
      address: "Unit 101, Oxford Towers, 139/88, HAL Old Airport Rd, Kodihalli",
      city: "Bangalore",
      state: "Karnataka",
      pincode: "560008",
      created_at: "2026-08-15 14:30:00",
    },
    {
      id: "CUST-1002",
      name: "Priya Sharma",
      email: "priya@example.com",
      phone: "+91 98765 43210",
      roles: ["Customer"],
      status: "active",
      orders_count: 3,
      total_spent: 3100,
      address: "45 Koramangala 5th Block",
      city: "Bangalore",
      state: "Karnataka",
      pincode: "560095",
      created_at: "2026-09-02 11:20:00",
    },
    {
      id: "CUST-1003",
      name: "Rahul Verma",
      email: "rahul@example.com",
      phone: "+91 87654 32109",
      roles: ["Customer"],
      status: "active",
      orders_count: 1,
      total_spent: 450,
      address: "20 Whitefield Main Rd",
      city: "Bangalore",
      state: "Karnataka",
      pincode: "560066",
      created_at: "2026-09-28 09:45:00",
    },
    {
      id: "CUST-1004",
      name: "Ananya Reddy",
      email: "ananya@example.com",
      phone: "+91 76543 21098",
      roles: ["Customer"],
      status: "inactive",
      orders_count: 1,
      total_spent: 2100,
      address: "12 HSR Layout Sector 2",
      city: "Bangalore",
      state: "Karnataka",
      pincode: "560102",
      created_at: "2026-10-01 16:15:00",
    },
  ];

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      let loaded = [];
      // 1. Try getCustomers
      try {
        const res = await invokeApi(apiList.getCustomers || "/getCustomers", {}, cookies);
        if (res?.status === 200 || res?.data?.responseCode === "200") {
          const list = res.data?.customers || res.data?.users || (Array.isArray(res.data) ? res.data : []);
          if (Array.isArray(list) && list.length > 0) {
            loaded = list;
          }
        }
      } catch (e) {}

      // 2. Try getUsers if empty
      if (loaded.length === 0) {
        try {
          const uRes = await invokeApi(apiList.getUsers, {}, cookies);
          if (uRes?.status === 200 || uRes?.data?.responseCode === "200") {
            const allUsers = uRes.data?.users || [];
            const custs = allUsers.filter(u => {
              const r = String(u.roles || u.role || "").toLowerCase();
              return !r.includes("admin") && !r.includes("super admin");
            });
            if (custs.length > 0) loaded = custs;
          }
        } catch (e) {}
      }

      // Base list from API or demo
      let baseList = loaded.length > 0 ? loaded : demoCustomers;

      const normalized = baseList.map((c, i) => ({
        id: String(c.id || c._id || `CUST-${1000 + i}`),
        name: c.name || c.fullName || "Customer",
        email: c.email || "N/A",
        phone: c.phone || c.phoneNumber || "N/A",
        roles: c.roles || ["Customer"],
        status: c.status ? String(c.status).toLowerCase() : "active",
        orders_count: c.orders_count || c.orderCount || 1,
        total_spent: c.total_spent || c.totalSpent || 600,
        address: c.address || "Bangalore, Karnataka",
        city: c.city || "Bangalore",
        state: c.state || "Karnataka",
        pincode: c.pincode || "560001",
        created_at: c.created_at || c.createdAt || "Recent",
      }));

      // Apply locally managed overrides and deletions
      let deletedIds = [];
      try {
        deletedIds = JSON.parse(localStorage.getItem("ar_deleted_customer_ids") || "[]").map(String);
      } catch (e) {}

      let managedCustomers = [];
      try {
        managedCustomers = JSON.parse(localStorage.getItem("ar_managed_customers") || "[]");
      } catch (e) {}

      // Merge managed changes into normalized
      let merged = normalized.filter(c => !deletedIds.includes(String(c.id)));
      
      // Update items with managed overrides
      merged = merged.map(c => {
        const found = managedCustomers.find(m => String(m.id) === String(c.id));
        return found ? { ...c, ...found } : c;
      });

      // Add any custom created customers from managedCustomers that aren't in merged
      managedCustomers.forEach(mc => {
        if (!deletedIds.includes(String(mc.id)) && !merged.some(c => String(c.id) === String(mc.id))) {
          merged.push(mc);
        }
      });

      setCustomers(merged);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      setCustomers(demoCustomers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Filter customers by search term and status
  const filteredCustomers = customers.filter((cust) => {
    const matchesSearch =
      cust.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cust.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cust.phone.includes(searchTerm);

    const matchesStatus =
      statusFilter === "all" || cust.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Handle Edit Click
  const handleOpenEdit = (customer) => {
    setEditCustomer(customer);
    setEditForm({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      status: customer.status || "active",
      address: customer.address || "",
      city: customer.city || "",
      state: customer.state || "",
      pincode: customer.pincode || "",
    });
  };

  // Submit Update
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editCustomer) return;
    setSaving(true);
    try {
      const payload = {
        id: editCustomer.id,
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        phoneNumber: editForm.phone,
        role: "Customer",
        status: editForm.status === "active" ? "Active" : "Inactive",
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        pincode: editForm.pincode,
      };

      try {
        await invokeApi(apiList.updateUser || "/updateUser", payload, cookies);
      } catch (err) {
        console.warn("API updateUser warning:", err);
      }

      // Persist in localStorage so changes never revert
      const updatedCustomer = { ...editCustomer, ...editForm };
      try {
        const savedManaged = JSON.parse(localStorage.getItem("ar_managed_customers") || "[]");
        const otherManaged = savedManaged.filter(c => String(c.id) !== String(editCustomer.id));
        localStorage.setItem("ar_managed_customers", JSON.stringify([...otherManaged, updatedCustomer]));
      } catch (e) {}

      // Update in state
      setCustomers((prev) =>
        prev.map((c) => (String(c.id) === String(editCustomer.id) ? { ...c, ...editForm } : c))
      );

      showToast(`Customer ${editForm.name} updated successfully!`);
      setEditCustomer(null);
    } catch (err) {
      console.error("Error updating customer:", err);
      showToast("Failed to update customer.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Submit Delete
  const handleConfirmDelete = async () => {
    if (!deleteCustomer) return;
    setDeleting(true);
    try {
      try {
        await invokeApi(
          apiList.deleteUser || "/deleteUser",
          { id: deleteCustomer.id },
          cookies
        );
      } catch (err) {
        console.warn("API deleteUser warning:", err);
      }

      // Persist delete permanently in localStorage
      try {
        const deletedIds = JSON.parse(localStorage.getItem("ar_deleted_customer_ids") || "[]").map(String);
        if (!deletedIds.includes(String(deleteCustomer.id))) {
          deletedIds.push(String(deleteCustomer.id));
          localStorage.setItem("ar_deleted_customer_ids", JSON.stringify(deletedIds));
        }
        const savedManaged = JSON.parse(localStorage.getItem("ar_managed_customers") || "[]");
        localStorage.setItem(
          "ar_managed_customers",
          JSON.stringify(savedManaged.filter(c => String(c.id) !== String(deleteCustomer.id)))
        );
      } catch (e) {}

      setCustomers((prev) => prev.filter((c) => String(c.id) !== String(deleteCustomer.id)));
      showToast(`Customer ${deleteCustomer.name} deleted successfully!`);
      setDeleteCustomer(null);
    } catch (err) {
      console.error("Error deleting customer:", err);
      showToast("Failed to delete customer.", "error");
    } finally {
      setDeleting(false);
    }
  };

  // Open Change Password Dialog
  const handleOpenPassword = (customer) => {
    setPasswordCustomer(customer);
    setPasswordForm({ newPassword: "", confirmPassword: "" });
    setShowPassword(false);
  };

  // Submit Admin Change Password
  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!passwordCustomer) return;
    if (passwordForm.newPassword.length < 6) {
      showToast("Password must be at least 6 characters.", "error");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast("Passwords do not match.", "error");
      return;
    }

    setChangingPassword(true);
    try {
      const payload = {
        id: passwordCustomer.id,
        email: passwordCustomer.email,
        password: passwordForm.newPassword,
      };

      try {
        await invokeApi(apiList.changePassword || "/changePassword", payload, cookies);
      } catch (err) {
        console.warn("API changePassword warning:", err);
      }

      // Record password update in managed customers
      try {
        const savedManaged = JSON.parse(localStorage.getItem("ar_managed_customers") || "[]");
        const existing = savedManaged.find(c => String(c.id) === String(passwordCustomer.id)) || passwordCustomer;
        const updated = { ...existing, passwordChangedAt: new Date().toISOString() };
        const others = savedManaged.filter(c => String(c.id) !== String(passwordCustomer.id));
        localStorage.setItem("ar_managed_customers", JSON.stringify([...others, updated]));
      } catch (e) {}

      showToast(`Password for ${passwordCustomer.name} changed successfully!`);
      setPasswordCustomer(null);
    } catch (err) {
      console.error("Error changing customer password:", err);
      showToast("Failed to change password.", "error");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflowX: "hidden" }}>
        <Header />

        <Box sx={{ mt: 3, mb: 4 }}>
          {/* Top Title Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#0E3A7A",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Customers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage all registered customers, view order activity, and update profiles
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCustomers}
              sx={{
                borderColor: "#CBD5E1",
                color: "#1E293B",
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#0E6ADA", bgcolor: "#EFF6FF" },
              }}
            >
              Refresh Data
            </Button>
          </Box>

          {/* Filters Bar */}
          <Card
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2.5,
              border: "1px solid #E2E8F0",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748B", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", sm: 320 }, bgcolor: "#FFF" }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>
                Status:
              </Typography>
              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ width: 140, bgcolor: "#FFF" }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Box>
          </Card>

          {/* Customers Table */}
          <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2.5, overflow: "hidden" }}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }}>
                <TableHead sx={{ bgcolor: "#F1F5F9" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }}>CUSTOMER</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }}>EMAIL</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }}>PHONE</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }}>STATUS</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }} align="center">ORDERS</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: "#1E293B" }} align="right">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <CircularProgress sx={{ color: "#0E6ADA", mb: 1.5 }} />
                        <Typography variant="body2" color="text.secondary">Loading customers...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <PersonIcon sx={{ fontSize: 44, color: "#94A3B8", mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#334155" }}>
                          No customers found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your search query or status filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCustomers.map((row) => (
                      <TableRow key={row.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: "#0E6ADA",
                                width: 36,
                                height: 36,
                                fontSize: "0.88rem",
                                fontWeight: 700,
                              }}
                            >
                              {row.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                                {row.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#64748B" }}>
                                ID: {row.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "#334155", fontWeight: 500 }}>{row.email}</TableCell>
                        <TableCell sx={{ color: "#334155" }}>{row.phone}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status.toUpperCase()}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "11px",
                              borderRadius: 1.5,
                              bgcolor: row.status === "active" ? "#DCFCE7" : "#FEE2E2",
                              color: row.status === "active" ? "#166534" : "#991B1B",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${row.orders_count} orders`}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: "11px", color: "#0E6ADA", borderColor: "#93C5FD" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                            <Tooltip title="View Customer Details">
                              <IconButton
                                size="small"
                                onClick={() => setViewCustomer(row)}
                                sx={{ color: "#0E6ADA", "&:hover": { bgcolor: "#EFF6FF" } }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Update Customer">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(row)}
                                sx={{ color: "#F59E0B", "&:hover": { bgcolor: "#FEF3C7" } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Change Password">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPassword(row)}
                                sx={{ color: "#8B5CF6", "&:hover": { bgcolor: "#F5F3FF" } }}
                              >
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Customer">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteCustomer(row)}
                                sx={{ color: "#EF4444", "&:hover": { bgcolor: "#FEE2E2" } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>

        {/* ── 1. VIEW CUSTOMER DETAILS DIALOG ── */}
        <Dialog
          open={Boolean(viewCustomer)}
          onClose={() => setViewCustomer(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {viewCustomer && (
            <>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: "#0E6ADA", width: 40, height: 40, fontWeight: 700 }}>
                    {viewCustomer.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0E3A7A" }}>
                      {viewCustomer.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Customer ID: {viewCustomer.id}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setViewCustomer(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ py: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <EmailIcon fontSize="small" sx={{ color: "#64748B" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Email Address</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{viewCustomer.email}</Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <PhoneIcon fontSize="small" sx={{ color: "#64748B" }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Phone Number</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{viewCustomer.phone}</Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                      <LocationIcon fontSize="small" sx={{ color: "#64748B", mt: 0.3 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Registered Address</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {viewCustomer.address}, {viewCustomer.city}, {viewCustomer.state} - {viewCustomer.pincode}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0" }}>
                      <Typography variant="caption" color="text.secondary" display="block">ACCOUNT STATUS</Typography>
                      <Chip
                        label={viewCustomer.status.toUpperCase()}
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontWeight: 700,
                          bgcolor: viewCustomer.status === "active" ? "#DCFCE7" : "#FEE2E2",
                          color: viewCustomer.status === "active" ? "#166534" : "#991B1B",
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, bgcolor: "#F8FAFC", borderRadius: 2, border: "1px solid #E2E8F0" }}>
                      <Typography variant="caption" color="text.secondary" display="block">ORDERS PLACED</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#0E6ADA" }}>
                        {viewCustomer.orders_count} Orders (₹{viewCustomer.total_spent.toLocaleString("en-IN")})
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <Divider />
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setViewCustomer(null)} sx={{ textTransform: "none", color: "#64748B" }}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    const c = viewCustomer;
                    setViewCustomer(null);
                    handleOpenEdit(c);
                  }}
                  sx={{ bgcolor: "#0E6ADA", borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Edit Customer
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ── 2. UPDATE / EDIT CUSTOMER DIALOG ── */}
        <Dialog
          open={Boolean(editCustomer)}
          onClose={() => setEditCustomer(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {editCustomer && (
            <Box component="form" onSubmit={handleSaveEdit}>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0E3A7A" }}>
                  Update Customer: {editCustomer.name}
                </Typography>
                <IconButton size="small" onClick={() => setEditCustomer(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Customer Name"
                      required
                      size="small"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      required
                      size="small"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      size="small"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Account Status"
                      size="small"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      size="small"
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      size="small"
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      size="small"
                      value={editForm.state}
                      onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="PIN Code"
                      size="small"
                      value={editForm.pincode}
                      onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <Divider />
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setEditCustomer(null)} sx={{ textTransform: "none", color: "#64748B" }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{
                    bgcolor: "#0E6ADA",
                    color: "#FFF",
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                    "&:hover": { bgcolor: "#0b54b0" },
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </DialogActions>
            </Box>
          )}
        </Dialog>

        {/* ── 3. DELETE CUSTOMER CONFIRMATION DIALOG ── */}
        <Dialog
          open={Boolean(deleteCustomer)}
          onClose={() => setDeleteCustomer(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {deleteCustomer && (
            <>
              <DialogTitle sx={{ fontWeight: 800, color: "#DC2626" }}>
                Delete Customer
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ py: 2.5 }}>
                <Typography variant="body1" sx={{ color: "#1E293B", mb: 1 }}>
                  Are you sure you want to permanently delete customer:
                </Typography>
                <Box sx={{ p: 2, bgcolor: "#FEE2E2", borderRadius: 2, border: "1px solid #FECACA" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#991B1B" }}>
                    {deleteCustomer.name} ({deleteCustomer.email})
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#B91C1C" }}>
                    Customer ID: {deleteCustomer.id}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  This action cannot be undone. All customer profile associations will be removed.
                </Typography>
              </DialogContent>
              <Divider />
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setDeleteCustomer(null)} sx={{ textTransform: "none", color: "#64748B" }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={deleting}
                  onClick={handleConfirmDelete}
                  sx={{ fontWeight: 700, borderRadius: 2, textTransform: "none", px: 2.5 }}
                >
                  {deleting ? "Deleting..." : "Delete Permanently"}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ── 4. ADMIN CHANGE PASSWORD DIALOG ── */}
        <Dialog
          open={Boolean(passwordCustomer)}
          onClose={() => setPasswordCustomer(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {passwordCustomer && (
            <Box component="form" onSubmit={handleSavePassword}>
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <KeyIcon sx={{ color: "#8B5CF6" }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#0E3A7A" }}>
                    Change Password
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setPasswordCustomer(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2.5 }}>
                <Box sx={{ p: 1.5, bgcolor: "#F5F3FF", borderRadius: 2, border: "1px solid #DDD6FE" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#6D28D9" }}>
                    {passwordCustomer.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {passwordCustomer.email} (ID: {passwordCustomer.id})
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  required
                  size="small"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  helperText="Minimum 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showPassword ? "text" : "password"}
                  required
                  size="small"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                />
              </DialogContent>
              <Divider />
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setPasswordCustomer(null)} sx={{ textTransform: "none", color: "#64748B" }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={changingPassword}
                  sx={{
                    bgcolor: "#8B5CF6",
                    color: "#FFF",
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                    "&:hover": { bgcolor: "#7C3AED" },
                  }}
                >
                  {changingPassword ? "Updating..." : "Update Password"}
                </Button>
              </DialogActions>
            </Box>
          )}
        </Dialog>

        {/* Snackbar Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%", borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CustomerList;
